import { buildQuery, requestJson } from './client'

export const listeningSignalTypes = [
  'bad_audio_quality',
  'neutral_audio_quality',
  'good_audio_quality',
  'boring_track',
  'neutral_track',
  'great_track',
  'poor_channel_fit',
  'neutral_channel_fit',
  'great_channel_fit'
] as const

export type ListeningSignalType = typeof listeningSignalTypes[number]
export type ListeningSignalGroupBy = 'channel' | 'prompt' | 'track'

export type ListeningSignalPlaybackMode = 'live_stream' | 'personal_queue'

export type ListeningSignal = {
  id: string
  admin_user_id: string
  channel_id: string
  audio_track_id: string
  suno_prompt_id: string | null
  signal_type: ListeningSignalType
  playback_mode: ListeningSignalPlaybackMode
  created_at: string
}

export type ListeningSignalSummaryItem = {
  target_id: string | null
  target_name: string
  channel_id: string | null
  channel_name: string | null
  prompt_id: string | null
  prompt_title: string | null
  track_id: string | null
  track_title: string | null
  track_artist: string | null
  total_count: string
  positive_count: string
  neutral_count: string
  negative_count: string
  bad_audio_quality_count: string
  neutral_audio_quality_count: string
  good_audio_quality_count: string
  boring_track_count: string
  neutral_track_count: string
  great_track_count: string
  poor_channel_fit_count: string
  neutral_channel_fit_count: string
  great_channel_fit_count: string
}

export type ListeningSignalSummaryFilters = {
  channelId?: string | undefined
  from?: string | undefined
  groupBy?: ListeningSignalGroupBy | undefined
  page?: number | undefined
  pageSize?: number | undefined
  signalType?: ListeningSignalType | undefined
  to?: string | undefined
}

export type ListeningSignalSummary = {
  groupBy: ListeningSignalGroupBy
  items: ListeningSignalSummaryItem[]
  page: number
  pageSize: number
  total: number
}

export const createListeningSignal = async (input: {
  audioTrackId?: string | undefined
  channelId: string
  playbackMode?: ListeningSignalPlaybackMode | undefined
  signalType: ListeningSignalType
}): Promise<ListeningSignal> => {
  const payload = await requestJson<{ item: ListeningSignal }>('/api/admin/listening-signals', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(input)
  })

  return payload.item
}

export const getCurrentListeningSignals = async (filters: {
  audioTrackId: string
  channelId: string
}): Promise<ListeningSignal[]> => {
  const payload = await requestJson<{ items: ListeningSignal[] }>(`/api/admin/listening-signals/current${buildQuery({
    audioTrackId: filters.audioTrackId,
    channelId: filters.channelId
  })}`)

  return payload.items
}

export const getListeningSignalSummary = async (
  filters: ListeningSignalSummaryFilters = {}
): Promise<ListeningSignalSummary> => {
  return requestJson<ListeningSignalSummary>(`/api/admin/listening-signals/summary${buildQuery({
    channelId: filters.channelId,
    from: filters.from,
    groupBy: filters.groupBy,
    page: filters.page,
    pageSize: filters.pageSize,
    signalType: filters.signalType,
    to: filters.to
  })}`)
}
